import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiService } from "../api/api";
import { STORAGE_KEYS } from "utils/constants";

export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const data = await apiService.auth.login(credentials);

      if (!data.success) {
        return rejectWithValue(data.message || "Login failed");
      }

      if (typeof window !== "undefined" && data.data?.user) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.data.user));
      }

      return { user: data.data.user };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Network error occurred"
      );
    }
  }
);

export const signupUser = createAsyncThunk(
  "auth/signup",
  async (userData, { rejectWithValue }) => {
    try {
      const data = await apiService.auth.signup(userData);

      if (!data.success) {
        return rejectWithValue(data.message || "Signup failed");
      }

      if (typeof window !== "undefined" && data.data?.user) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.data.user));
      }

      return { user: data.data.user };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Network error occurred"
      );
    }
  }
);

export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.users.me();

      if (!response.success) {
        return rejectWithValue(response.message || "Failed to fetch user");
      }

      const user = response.data?.user || response.data;

      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      }

      return user;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch user"
      );
    }
  }
);

const getInitialState = () => {
  const initialState = {
    user: null,
    loading: false,
    error: null,
  };

  if (typeof window !== "undefined") {
    const savedUser = localStorage.getItem(STORAGE_KEYS.USER);

    if (savedUser) {
      try {
        initialState.user = JSON.parse(savedUser);
      } catch {
        localStorage.removeItem(STORAGE_KEYS.USER);
      }
    }
  }

  return initialState;
};

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    logout: (state) => {
      state.user = null;
      state.error = null;
      state.loading = false;

      if (typeof window !== "undefined") {
        localStorage.removeItem(STORAGE_KEYS.USER);
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload.user || action.payload;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.error = action.payload;
      })
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.error = action.payload;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.error = null;
      });
  },
});

export const { logout, clearError, setUser } = authSlice.actions;

export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectToken = () => null; // tokens are now httpOnly cookies
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectIsAuthenticated = (state) => !!state.auth.user;

export default authSlice.reducer;
